import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getLicenseById, startInstance, stopInstance } from "../../../../actions/licensesActions";
import { getServerUrls } from "../../../../actions/serverUrlActions";
import { createShop, deployShop, getAvailableShopVersions, updateShop } from "../../../../actions/shopActions";
import Config from "../../../../Config";
import {
    FGWEBSHOP_DEPLOY_STATUS_DEPLOY_STARTED,
    FGWEBSHOP_DEPLOY_STATUS_ERROR,
    FGWEBSHOP_DEPLOY_STATUS_INSTANCE_CREATED,
    FGWEBSHOP_DEPLOY_STATUS_INSTANCE_DEACTIVATED,
    FGWEBSHOP_DEPLOY_STATUS_INSTANCE_RUNNING,
    FGWEBSHOP_DEPLOY_STATUS_INSTANCE_STOPPED,
    FGWEBSHOP_DEPLOY_STATUS_NOT_DEPLOYED,
} from "../../../../constants/fgWebshopDeployStatus";
import { getLicmanShopDeployAlertMessage } from "../../../../helpers/shopDeployMessages";
import "./WebShops.scss";

const config = new Config();

const FGWEBSHOP_KIND = "fgwebshop";

function formatDeployStatusLabel(shopKind, deployStatus) {
    if (shopKind === "fgwebshop") {
        switch (deployStatus) {
            case FGWEBSHOP_DEPLOY_STATUS_NOT_DEPLOYED:
                return "Nicht bereitgestellt";
            case FGWEBSHOP_DEPLOY_STATUS_DEPLOY_STARTED:
                return "Bereitstellung gestartet";
            case FGWEBSHOP_DEPLOY_STATUS_INSTANCE_CREATED:
                return "Instanz angelegt";
            case FGWEBSHOP_DEPLOY_STATUS_INSTANCE_RUNNING:
                return "Aktiv";
            case FGWEBSHOP_DEPLOY_STATUS_INSTANCE_DEACTIVATED:
                return "Deaktiviert";
            case FGWEBSHOP_DEPLOY_STATUS_INSTANCE_STOPPED:
                return "Instanz gestoppt";
            case FGWEBSHOP_DEPLOY_STATUS_ERROR:
                return "Bereitstellung fehlgeschlagen";
            default:
                break;
        }
    }
    return deployStatus != null ? String(deployStatus) : "";
}

function showWebshopStartButton(row) {
    if (row.shopKind !== FGWEBSHOP_KIND) {
        return false;
    }
    const status = row.deployStatus;
    return (
        status === FGWEBSHOP_DEPLOY_STATUS_INSTANCE_DEACTIVATED || status === FGWEBSHOP_DEPLOY_STATUS_INSTANCE_STOPPED
    );
}

function showWebshopStopButton(row) {
    if (row.shopKind !== FGWEBSHOP_KIND) {
        return false;
    }
    return row.deployStatus === FGWEBSHOP_DEPLOY_STATUS_INSTANCE_RUNNING;
}
const WebShops = ({ licenseId }) => {
    const [license, setLicense] = useState(null);
    const [availableFgWebshopVersions, setAvailableFgWebshopVersions] = useState([]);
    /** Pending Deploy/Start/Stop for one shop row; blocks that row until the request finishes. */
    const [pendingRowAction, setPendingRowAction] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchData = async () => {
            const licenseData = await dispatch(getLicenseById(licenseId));
            setLicense(licenseData);

            const versions = await dispatch(getAvailableShopVersions(FGWEBSHOP_KIND));
            setAvailableFgWebshopVersions(Array.isArray(versions) ? versions : []);

            await dispatch(getServerUrls());
        };

        fetchData();
    }, [dispatch, licenseId]);

    const handleCreate = async (newRow) => {
        const image = config.apiUrl.includes("dev")
            ? "develop"
            : config.apiUrl.includes("staging")
              ? "staging"
              : "main";
        const updatedRow = { ...newRow, licenseId: Number(licenseId), image };
        await dispatch(createShop(updatedRow));
        const updatedLicense = await dispatch(getLicenseById(licenseId));
        setLicense(updatedLicense);
    };

    const handleUpdate = async (updatedRow) => {
        await dispatch(updateShop(updatedRow));
        const updatedLicense = await dispatch(getLicenseById(licenseId));
        setLicense(updatedLicense);
    };

    const processRowUpdate = async (newRow, oldRow) => {
        const same =
            newRow.shopName === oldRow.shopName &&
            newRow.url === oldRow.url &&
            (newRow.image || "") === (oldRow.image || "");
        if (same) {
            return newRow;
        }
        await handleUpdate(newRow);
        return newRow;
    };

    const handleDeploy = async (shopId) => {
        const id = Number(shopId);
        setPendingRowAction({ shopId: id, action: "deploy" });
        try {
            await dispatch(deployShop(Number(licenseId), id));
            const updatedLicense = await dispatch(getLicenseById(licenseId));
            setLicense(updatedLicense);
        } catch (e) {
            window.alert(getLicmanShopDeployAlertMessage(e));
        } finally {
            setPendingRowAction(null);
        }
    };

    const handleStartShop = async (url, shopId) => {
        const id = Number(shopId);
        setPendingRowAction({ shopId: id, action: "start" });
        try {
            await dispatch(startInstance(FGWEBSHOP_KIND, url.replaceAll(".", "-"), Number(licenseId), id));
            const updatedLicense = await dispatch(getLicenseById(licenseId));
            setLicense(updatedLicense);
        } catch (e) {
            window.alert(getLicmanShopDeployAlertMessage(e));
        } finally {
            setPendingRowAction(null);
        }
    };

    const handleStopShop = async (url, shopId) => {
        const id = Number(shopId);
        setPendingRowAction({ shopId: id, action: "stop" });
        try {
            await dispatch(stopInstance(FGWEBSHOP_KIND, url.replaceAll(".", "-"), Number(licenseId), id));
            const updatedLicense = await dispatch(getLicenseById(licenseId));
            setLicense(updatedLicense);
        } catch (e) {
            window.alert(getLicmanShopDeployAlertMessage(e));
        } finally {
            setPendingRowAction(null);
        }
    };

    const columns = [
        { field: "shopName", headerName: "Name", flex: 1, editable: true, width: 100 },
        { field: "url", headerName: "ShopUrl", flex: 1, editable: true },
        {
            field: "image",
            headerName: "Image",
            flex: 1,
            editable: true,
            renderCell: (params) => {
                const currentImage = params.value || "";
                return (
                    <select
                        value={currentImage}
                        onChange={(e) => {
                            handleUpdate({ ...params.row, image: e.target.value });
                        }}
                    >
                        {availableFgWebshopVersions.map((fullImageRef) => (
                            <option key={fullImageRef} value={fullImageRef}>
                                {fullImageRef.includes(":") ? fullImageRef.split(":").pop() : fullImageRef}
                            </option>
                        ))}
                    </select>
                );
            },
        },
        {
            field: "deployStatus",
            headerName: "Deploy Status",
            flex: 1,
            renderCell: (params) => formatDeployStatusLabel(params.row.shopKind, params.value),
        },
        {
            field: "deactivatedAt",
            headerName: "Deaktiviert am",
            flex: 1,
            renderCell: (params) => (params.value ? new Date(params.value).toLocaleString("de-DE") : "—"),
        },
        { field: "keycloakRealmName", headerName: "Keycloak Realm", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => {
                const sid = Number(params.row.shopId);
                const rowBusy = pendingRowAction?.shopId === sid;
                const loadingDeploy = rowBusy && pendingRowAction?.action === "deploy";
                const loadingStart = rowBusy && pendingRowAction?.action === "start";
                const loadingStop = rowBusy && pendingRowAction?.action === "stop";

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            height: "100%",
                            gap: 1,
                        }}
                    >
                        {params.row.shopKind === FGWEBSHOP_KIND &&
                            (params.row.deployStatus == null ||
                                params.row.deployStatus === FGWEBSHOP_DEPLOY_STATUS_NOT_DEPLOYED) && (
                                <Button
                                    onClick={() => handleDeploy(params.row.shopId)}
                                    variant="contained"
                                    size="small"
                                    disabled={rowBusy}
                                    aria-busy={loadingDeploy}
                                    sx={{ minWidth: loadingDeploy ? 100 : undefined }}
                                >
                                    {loadingDeploy ? (
                                        <CircularProgress size={18} color="inherit" aria-hidden />
                                    ) : (
                                        "Deploy"
                                    )}
                                </Button>
                            )}
                        {showWebshopStartButton(params.row) && (
                            <Button
                                onClick={() => handleStartShop(params.row.url, params.row.shopId)}
                                variant="contained"
                                size="small"
                                disabled={rowBusy}
                                aria-busy={loadingStart}
                                sx={{ minWidth: loadingStart ? 100 : undefined }}
                            >
                                {loadingStart ? <CircularProgress size={18} color="inherit" aria-hidden /> : "Starten"}
                            </Button>
                        )}
                        {showWebshopStopButton(params.row) && (
                            <Button
                                onClick={() => handleStopShop(params.row.url, params.row.shopId)}
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={rowBusy}
                                aria-busy={loadingStop}
                                sx={{ minWidth: loadingStop ? 100 : undefined }}
                            >
                                {loadingStop ? <CircularProgress size={18} color="inherit" aria-hidden /> : "Stoppen"}
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <Box className="webshops" sx={{ height: "80vh", width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h4">Webshops</Typography>
                <Button variant="contained" color="primary" onClick={() => handleCreate({ shopKind: FGWEBSHOP_KIND })}>
                    Add Online Shop
                </Button>
            </Box>
            {license && license.Shops && (
                <DataGrid
                    rows={license.Shops}
                    columns={columns}
                    getRowId={(row) => row.shopId}
                    disableSelectionOnClick
                    autoHeight
                    components={{
                        Toolbar: GridToolbar,
                    }}
                    editMode="cell"
                    processRowUpdate={processRowUpdate}
                    sx={{
                        "& .MuiDataGrid-root": {
                            border: "none",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default WebShops;
